import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { Home, Compass } from "lucide-react";
import { acme, acmeMarketingPageSx } from "../../theme/acmeTheme";
import { Button } from "../ui";

const errorMessages = {
  404: {
    title: "404",
    description: (
      <>
        The page you&apos;re looking for might have been <br />
        moved or doesn&apos;t exist.
      </>
    ),
  },
  500: {
    title: "500",
    description: (
      <>
        Our servers hit a snag. <br />
        We&apos;re working to fix it — please try again soon.
      </>
    ),
  },
  "network-error": {
    title: "Offline",
    description: (
      <>
        Unable to reach our servers. <br />
        Check your connection and try again.
      </>
    ),
  },
  maintenance: {
    title: "503",
    description: (
      <>
        Scheduled maintenance in progress. <br />
        We&apos;ll be back shortly.
      </>
    ),
  },
  403: {
    title: "403",
    description: (
      <>
        You don&apos;t have permission to access this page. <br />
        Contact your administrator if you need access.
      </>
    ),
  },
  default: {
    title: "Error",
    description: (
      <>
        Something went wrong on our end. <br />
        Please try again or return home.
      </>
    ),
  },
};

const codeTitleSx = {
  fontWeight: 800,
  fontSize: { xs: "6rem", sm: "8rem", md: "9rem" },
  lineHeight: 1,
  letterSpacing: "-0.04em",
  background: `linear-gradient(180deg, ${acme.colors.foreground} 22%, rgba(10, 10, 10, 0.12) 82%)`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
};

export default function ErrorPage({ statusCode = 404 }) {
  const navigate = useNavigate();
  const [errorDetails, setErrorDetails] = useState(null);

  useEffect(() => {
    const storedError = sessionStorage.getItem("lastApiError");
    if (!storedError) return;
    try {
      setErrorDetails(JSON.parse(storedError));
      sessionStorage.removeItem("lastApiError");
    } catch (e) {
      console.error("Failed to parse stored error:", e);
    }
  }, []);

  const effectiveStatusCode = errorDetails?.status || statusCode;
  const message = errorMessages[effectiveStatusCode] || errorMessages.default;
  const showTechnicalDetails =
    process.env.NODE_ENV === "development" && errorDetails?.message;

  return (
    <Box
      sx={{
        ...acmeMarketingPageSx,
        position: "relative",
        display: "flex",
        minHeight: "100vh",
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        backgroundColor: acme.colors.background,
        px: { xs: 2, sm: 3 },
        py: { xs: 6, sm: 8 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          minWidth: 0,
          flex: 1,
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 3,
          textAlign: "center",
          maxWidth: 480,
        }}
      >
        <Box
          sx={{
            display: "flex",
            maxWidth: 420,
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography component="h1" sx={codeTitleSx}>
            {message.title}
          </Typography>
          <Typography
            sx={{
              mt: -2,
              color: "rgba(10, 10, 10, 0.78)",
              fontSize: { xs: "0.9375rem", sm: "1rem" },
              lineHeight: 1.65,
            }}
          >
            {message.description}
          </Typography>
        </Box>

        {showTechnicalDetails && (
          <Box
            sx={{
              width: "100%",
              p: 2,
              borderRadius: acme.radius.md,
              border: `1px solid ${acme.colors.border}`,
              backgroundColor: acme.colors.pageBg,
              textAlign: "left",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                color: acme.colors.mutedForegroundSolid,
                mb: 0.75,
              }}
            >
              Technical details
            </Typography>
            <Typography
              component="pre"
              sx={{
                m: 0,
                fontSize: "0.75rem",
                color: acme.colors.mutedForegroundSolid,
                whiteSpace: "pre-wrap",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              }}
            >
              {errorDetails.message}
            </Typography>
            <Typography sx={{ mt: 1, fontSize: "0.6875rem", color: acme.colors.mutedForegroundSolid }}>
              {new Date(errorDetails.timestamp).toLocaleString()}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            justifyContent: "center",
            width: "100%",
            maxWidth: 360,
          }}
        >
          <Button
            component={Link}
            to="/"
            size="lg"
            startIcon={<Home size={16} strokeWidth={2} />}
          >
            Go Home
          </Button>
          <Button
            component={Link}
            to="/about"
            variant="outline"
            size="lg"
            startIcon={<Compass size={16} strokeWidth={2} />}
          >
            Explore
          </Button>
        </Box>

        {effectiveStatusCode !== 404 && (
          <Button
            variant="link"
            onClick={() => navigate(-1)}
            sx={{ fontSize: "0.875rem" }}
          >
            Go back
          </Button>
        )}
      </Box>
    </Box>
  );
}
